import type {
  ServerFileEntry,
  ServerPendingFileEntry,
  ServerStateResponse
} from './serverStateDecoder';

export enum FileComparisonStatus {
  NEW = 'new',
  MODIFIED = 'modified',
  UNCHANGED = 'unchanged',
  FAILED = 'failed'
}

export interface ComparedFile {
  file: File;
  relativePath: string;
  status: FileComparisonStatus;
  selected: boolean;
  serverModifiedOn?: string;
  localModifiedOn: string;
}

export interface FileComparisonResult {
  files: ComparedFile[];
  totalCount: number;
  newCount: number;
  modifiedCount: number;
  unchangedCount: number;
  failedCount: number;
  existingCount: number;
}

export function compareFilesWithServer(
  localFiles: File[],
  serverState: ServerStateResponse
): FileComparisonResult {
  const existingMap = new Map<string, ServerFileEntry>();
  for (const sf of serverState.existing_files ?? []) {
    existingMap.set(sf.rp, sf);
  }

  const pendingMap = new Map<string, ServerPendingFileEntry>();
  for (const pf of serverState.pending_files ?? []) {
    pendingMap.set(pf.rp, pf as ServerPendingFileEntry);
  }

  const files: ComparedFile[] = localFiles.map((file) => {
    const relativePath = (file as any).webkitRelativePath || file.name;
    const localModifiedOn = new Date(file.lastModified).toISOString();

    const pendingEntry = pendingMap.get(relativePath);
    if (pendingEntry?.st === 'failed') {
      return {
        file,
        relativePath,
        status: FileComparisonStatus.FAILED,
        selected: true,
        serverModifiedOn: pendingEntry.fmo ?? undefined,
        localModifiedOn
      };
    }

    const serverEntry = existingMap.get(relativePath);

    if (!serverEntry) {
      return {
        file,
        relativePath,
        status: FileComparisonStatus.NEW,
        selected: true,
        localModifiedOn
      };
    }

    if (serverEntry.fmo) {
      const serverDate = new Date(serverEntry.fmo).getTime();
      const localDate = new Date(file.lastModified).getTime();
      const isModified = Math.abs(serverDate - localDate) > 1000;

      if (!isModified) {
        return {
          file,
          relativePath,
          status: FileComparisonStatus.UNCHANGED,
          selected: false,
          serverModifiedOn: serverEntry.fmo,
          localModifiedOn
        };
      }
    }

    return {
      file,
      relativePath,
      status: FileComparisonStatus.MODIFIED,
      selected: true,
      serverModifiedOn: serverEntry.fmo ?? undefined,
      localModifiedOn
    };
  });

  const newCount = files.filter((f) => f.status === FileComparisonStatus.NEW).length;
  const modifiedCount = files.filter((f) => f.status === FileComparisonStatus.MODIFIED).length;
  const unchangedCount = files.filter((f) => f.status === FileComparisonStatus.UNCHANGED).length;
  const failedCount = files.filter((f) => f.status === FileComparisonStatus.FAILED).length;

  return {
    files,
    totalCount: files.length,
    newCount,
    modifiedCount,
    unchangedCount,
    failedCount,
    existingCount: modifiedCount + unchangedCount
  };
}
